import React, { useMemo } from "react";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { useSelector } from "react-redux";

/**
 * Maps Admin/API website theme colors → MUI theme
 * Brand: E & J Global Realtors (pink → purple)
 */
const BrandThemeProvider = ({ children }) => {
  const colors = useSelector((state) => state.themeValue?.colors) || {};
  const fontSize = useSelector((state) => state.themeValue?.fontSize) || {};

  const theme = useMemo(() => {
    const primary = colors.primary || "#E91E8C";
    const gradientStart = colors.primary_gradient_start_color || "#FF2D95";
    const gradientEnd = colors.primary_gradient_end_color || "#7B1FA2";

    return createTheme({
      palette: {
        mode: "light",
        primary: {
          main: primary,
          light: gradientStart,
          dark: gradientEnd,
          contrastText: "#FFFFFF",
        },
        secondary: {
          main: gradientEnd,
          light: "#CE93D8",
          dark: "#4A148C",
          contrastText: "#FFFFFF",
        },
        background: {
          default: colors.screen_background || "#FBF7FC",
          paper: colors.background || "#FFFFFF",
        },
        text: {
          primary: colors.text || "#262525",
          secondary: colors.secondary_text_color || "#6B6B6B",
        },
        divider: colors.borderColor || "#E1BEE7",
      },
      typography: {
        fontSize: fontSize.medium || 16,
      },
      shape: {
        borderRadius: 10,
      },
      components: {
        MuiButton: {
          styleOverrides: {
            containedPrimary: {
              background: `linear-gradient(90deg, ${gradientStart}, ${gradientEnd})`,
              color: "#FFFFFF",
              boxShadow: "none",
              "&:hover": {
                background: `linear-gradient(90deg, ${gradientEnd}, ${gradientStart})`,
                color: "#FFFFFF",
                boxShadow: "none",
              },
              "& .MuiTypography-root": {
                color: "#FFFFFF",
              },
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: colors.screen_background || "#FBF7FC",
            },
          },
        },
      },
      // Expose brand tokens for custom components
      brand: {
        gradient: `linear-gradient(90deg, ${gradientStart}, ${gradientEnd})`,
        gradientStart,
        gradientEnd,
        primary,
        link: colors.link_color || "#C2185B",
        chipBg: colors.chip_background_color || "#F3E5F5",
        tabActive: colors.tab_bar_active || "#E91E8C",
        tabInactive: colors.tab_bar_inactive || "#CE93D8",
      },
    });
  }, [colors, fontSize]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default BrandThemeProvider;
